/**
 * useVerticalVocab - the single access point for vertical-specific labels.
 *
 * Every component that renders user-facing text should import this hook
 * and use it instead of hardcoding labels. This enables the entire UI
 * to change vocabulary when the tenant's vertical changes.
 *
 * Usage:
 *   const v = useVerticalVocab();
 *   <h1>{v.people.plural}</h1>          // "Members" or "Students" or "Patrons"
 *   <Button>{v.cases.addButton}</Button> // "New Support Request" or "New Match"
 */
import { useManifest } from "@/features/manifest/model/useManifest";

import {
  COMMUNITY_HUB,
  FOOD_ACCESS,
  YOUTH_PROGRAMS,
  FAITH_BASED,
  HEALTH_RECOVERY,
  ARTS_CULTURE,
  HOUSING,
  ADVOCACY,
  DEFAULT_VERTICAL,
} from "./configs";
import type { VerticalVocab, VerticalMap } from "./types";

/** All verticals keyed by ID */
export const VERTICALS: VerticalMap = {
  community_hub: COMMUNITY_HUB,
  food_access: FOOD_ACCESS,
  youth_programs: YOUTH_PROGRAMS,
  faith_based: FAITH_BASED,
  health_recovery: HEALTH_RECOVERY,
  arts_culture: ARTS_CULTURE,
  housing: HOUSING,
  advocacy: ADVOCACY,
  _default: DEFAULT_VERTICAL,
};

/**
 * Hook: returns the vocabulary config for the current tenant's vertical.
 *
 * Falls back to DEFAULT_VERTICAL if:
 * - The manifest hasn't loaded yet
 * - The tenant has no vertical assigned
 * - The vertical ID doesn't match any config
 *
 * This fallback ensures existing tenants (created before the vertical
 * system) see the current hardcoded labels - no breaking change.
 */
export function useVerticalVocab(): VerticalVocab {
  const { manifest } = useManifest();
  const vertical = manifest?.vertical as string | undefined;
  const base =
    !vertical || !VERTICALS[vertical] ? DEFAULT_VERTICAL : VERTICALS[vertical];
  return applyVocabOverrides(base, manifest?.vocab_overrides);
}

function applyVocabOverrides(
  base: VerticalVocab,
  overrides: Record<string, unknown> | undefined,
): VerticalVocab {
  if (!overrides || typeof overrides !== "object") return base;
  return mergeDeep(base, overrides) as VerticalVocab;
}

function mergeDeep(target: unknown, source: Record<string, unknown>): unknown {
  if (!target || typeof target !== "object" || Array.isArray(target)) {
    return source;
  }
  const out: Record<string, unknown> = { ...(target as Record<string, unknown>) };
  for (const [key, value] of Object.entries(source)) {
    if (value && typeof value === "object" && !Array.isArray(value)) {
      const existing = out[key];
      out[key] = mergeDeep(
        existing && typeof existing === "object" ? existing : {},
        value as Record<string, unknown>,
      );
    } else if (typeof value === "string" && value.trim()) {
      out[key] = value;
    }
  }
  return out;
}

// Re-export types for convenience
export type { VerticalVocab } from "./types";
